import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  PasswordInput,
  Select,
  SelectProps,
  Skeleton,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { Container, Grid2 as Grid } from "@mui/material";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconAt,
  IconEdit,
  IconEye,
  IconKey,
  IconRefresh,
  IconReload,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import dayjs from "dayjs";

import {
  BrandImages,
  Colors,
  iCloudStorageList,
  ServiceList,
} from "../../Lib/Data";
import { PlanProps, PossibleServices, UserProfileProps } from "../../Lib/Types";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  generateSecurePassword,
  parseString,
  showValidationErrors,
  validateEmail,
} from "../../Lib/Methods";
import { NonPaginatedResponse } from "../../Lib/Responses";
import {
  PerformRequest,
  UploadFiles,
  usePerformRequest,
} from "../../Lib/usePerformRequest";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import "./styles.scss";

const selectProps: SelectProps = {
  allowDeselect: false,
};

const SinglePlan: React.FC = () => {
  const { planID } = useParams();
  const navigate = useNavigate();

  // Plan data
  const {
    data: plan,
    isLoading: isLoadingPlan,
    reloadData: reloadPlan,
  } = usePerformRequest<NonPaginatedResponse<PlanProps>>({
    method: "GET",
    route: Endpoints.GetSinglePlan(planID as string),
  });

  // Users in the plan
  const [planUsers, setPlanUsers] = useState<UserProfileProps[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);

  // Update modal state
  const [
    isUpdateModalOpened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState<boolean>(false);
  const [tempSpotifyImage, setTempSpotifyImage] = useState<File>();
  const [isSpotifyImageUpdated, setIsSpotifyImageUpdated] =
    useState<boolean>(false);

  // Form for updating plan
  const updateForm = useForm<any>({
    initialValues: {
      id: "",
      subscriptionDate: dayjs().toString(),
      expirationDate: dayjs().add(30, "day").toString(),
      service: "Netflix" as PossibleServices,
      users: [],
      slots: 4,
      login: {
        email: "",
        password: "",
      },
      address: {
        image: "",
        value: "",
      },
      iCloudStorage: "10GB",
    },
    validate: {
      service: (value: PossibleServices) =>
        value && value.length > 0 ? null : "Please select a service",
      slots: (value: number) =>
        value && value > 0 ? null : "Enter number of available slots",
      login: {
        email: (value: string) =>
          validateEmail(value) ? null : "Enter a valid email address",
        password: (value: string) =>
          value.length >= 6 ? null : "Min: 6 Characters",
      },
      iCloudStorage: (value: any, values: any) =>
        values.service !== "iCloud - Apple Music"
          ? null
          : !value || value.length === 0
          ? "Select iCloud storage!"
          : null,
      address: {
        image: (value: any, values: any) =>
          values.service !== "Spotify Premium"
            ? null
            : tempSpotifyImage === undefined
            ? "Upload an image"
            : null,
        value: (value: any, values: any) =>
          values.service !== "Spotify Premium"
            ? null
            : value.length === 0
            ? "Enter address!"
            : null,
      },
    },
  });

  // Load users when plan data is available
  useEffect(() => {
    if (plan?.data && plan.data.users.length > 0) {
      loadPlanUsers();
    }
  }, [plan]);

  // Load users in the plan
  const loadPlanUsers = async () => {
    if (!plan?.data || plan.data.users.length === 0) return;

    setIsLoadingUsers(true);
    try {
      const response = await PerformRequest<
        NonPaginatedResponse<UserProfileProps[]>
      >({
        method: "POST",
        route: Endpoints.GetUsersByArray(),
        data: {
          ids: plan.data.users,
        },
      });

      if (response && response.status === 200 && response?.data?.data) {
        setPlanUsers(response.data.data);
      } else {
        DefaultErrorNotification("Could not load users!");
      }
    } catch (error) {
      DefaultErrorNotification("Could not load users!");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Handle plan update
  const handleUpdatePlan = async () => {
    const { hasErrors: formHasErrors } = updateForm.validate();

    if (formHasErrors) return;

    const {
      id,
      expirationDate,
      subscriptionDate,
      service,
      slots,
      login,
      address,
    } = updateForm.values;

    let data = {
      id,
      service,
      isFilledSlots: false,
      subscriptionDate: dayjs(subscriptionDate).toString(),
      expirationDate: dayjs(expirationDate).toString(),
      slots,
      login: {
        email: parseString(login.email, true),
        password: parseString(login.password),
      },
    };

    const update = async (data: any) => {
      setIsUpdatingPlan(true);
      try {
        const response = await PerformRequest({
          method: "POST",
          route: Endpoints.GetUpdateExistingPlan(),
          data,
        });

        if (response) {
          if (response.status === 403) {
            showValidationErrors(response);
          } else if (response.status === 200) {
            closeUpdateModal();
            reloadPlan();
            DefaultSuccessNotification("Plan updated successfully!");
          }
        }
      } catch (error) {
        DefaultErrorNotification("Failed to update plan!");
      } finally {
        setIsUpdatingPlan(false);
      }
    };

    if (service === "Spotify Premium") {
      if (isSpotifyImageUpdated && tempSpotifyImage) {
        setIsUpdatingPlan(true);
        try {
          const uploaded = await UploadFiles([tempSpotifyImage]);
          if (uploaded && uploaded.status === 201) {
            update({
              ...data,
              address: {
                image: uploaded?.data[0]?.path,
                value: address.value,
              },
            });
          }
        } catch (error) {
          setIsUpdatingPlan(false);
          DefaultErrorNotification("Could not upload image!");
        }
      } else {
        update({
          ...data,
          address: {
            image: address.image,
            value: address.value,
          },
        });
      }
    } else if (service === "iCloud - Apple Music") {
      update({
        ...data,
        iCloudStorage: updateForm.values.iCloudStorage,
      });
    } else {
      update(data);
    }
  };

  // Initialize update form with current plan data
  const initializeUpdateForm = () => {
    if (!plan?.data) return;

    updateForm.setValues({
      id: plan.data.id,
      subscriptionDate: plan.data.subscriptionDate,
      expirationDate: plan.data.expirationDate,
      service: plan.data.service,
      slots: plan.data.slots,
      login: plan.data.login,
      iCloudStorage: plan.data.iCloudStorage,
    });

    if (plan.data.service === "Spotify Premium" && (plan.data as any).address) {
      updateForm.setFieldValue("address", (plan.data as any).address);
    }

    openUpdateModal();
  };

  // Check if plan is expired
  const isPlanExpired = () => {
    if (!plan?.data) return true;
    return dayjs().isAfter(dayjs(plan.data.expirationDate));
  };

  // Check if plan has available slots
  const hasAvailableSlots = () => {
    if (!plan?.data) return false;
    return plan.data.slots > plan.data.users.length;
  };

  if (isLoadingPlan) {
    return (
      <Container maxWidth="lg">
        <MegaLoader size={50} />
      </Container>
    );
  }

  if (!plan?.data) {
    return (
      <Container maxWidth="lg">
        <DisplayAlert
          title="Plan Not Found"
          children={<Text>The requested plan could not be found.</Text>}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <div className="single-plan-container">
        {/* Header */}
        <div className="component-header flex-row align-center justify-between">
          <div className="flex-row align-center">
            <ActionIcon
              variant="subtle"
              onClick={() => navigate("/dashboard/plans")}
              mr={15}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Text className="label">Plan Details</Text>
              <Text c="dimmed" fz="sm">
                {plan.data.service} - {plan.data.id}
              </Text>
            </div>
          </div>
          <div className="flex-row align-center">
            <Button
              color={Colors.GreenPrimary}
              variant="outline"
              onClick={reloadPlan}
              loading={isLoadingPlan}
              rightSection={<IconReload size={16} />}
              mr={10}
            >
              Reload
            </Button>
            <Button
              color={Colors.BluePrimary}
              onClick={initializeUpdateForm}
              rightSection={<IconEdit size={16} />}
            >
              Edit Plan
            </Button>
          </div>
        </div>

        <br />

        {/* Plan Overview Cards */}
        <Grid spacing={3} container>
          <Grid size={{ xl: 3, lg: 4, md: 6, sm: 6, xs: 12 }}>
            <Card withBorder p="md">
              <div className="flex-row align-center justify-between">
                <div>
                  <Text c="dimmed" fz="sm" fw={500}>
                    Service
                  </Text>
                  <Text fz="lg" fw={600} c={Colors.BlackPrimary}>
                    {plan.data.service}
                  </Text>
                </div>
                <img
                  src={BrandImages.retrieveImage(plan.data.service)}
                  alt=""
                  style={{ width: 40, height: 40, objectFit: "contain" }}
                />
              </div>
            </Card>
          </Grid>

          <Grid size={{ xl: 3, lg: 4, md: 6, sm: 6, xs: 12 }}>
            <Card withBorder p="md">
              <Text c="dimmed" fz="sm" fw={500}>
                Available Slots
              </Text>
              <Text
                fz="lg"
                fw={600}
                c={
                  hasAvailableSlots() ? Colors.GreenPrimary : Colors.RedPrimary
                }
              >
                {plan.data.slots - plan.data.users.length} / {plan.data.slots}
              </Text>
              <Badge
                color={hasAvailableSlots() ? "green" : "red"}
                variant="light"
                size="sm"
                mt={5}
              >
                {hasAvailableSlots() ? "Open" : "Full"}
              </Badge>
            </Card>
          </Grid>

          <Grid size={{ xl: 3, lg: 4, md: 6, sm: 6, xs: 12 }}>
            <Card withBorder p="md">
              <Text c="dimmed" fz="sm" fw={500}>
                Subscription Date
              </Text>
              <Text fz="lg" fw={600} c={Colors.BlackPrimary}>
                {dayjs(plan.data.subscriptionDate).format("MMM DD, YYYY")}
              </Text>
            </Card>
          </Grid>

          <Grid size={{ xl: 3, lg: 4, md: 6, sm: 6, xs: 12 }}>
            <Card withBorder p="md">
              <Text c="dimmed" fz="sm" fw={500}>
                Expiration Date
              </Text>
              <Text
                fz="lg"
                fw={600}
                c={isPlanExpired() ? Colors.RedPrimary : Colors.GreenPrimary}
              >
                {dayjs(plan.data.expirationDate).format("MMM DD, YYYY")}
              </Text>
              <Badge
                color={isPlanExpired() ? "red" : "green"}
                variant="light"
                size="sm"
                mt={5}
              >
                {isPlanExpired() ? "Expired" : "Active"}
              </Badge>
            </Card>
          </Grid>
        </Grid>

        <br />

        {/* Plan Details Table */}
        <Paper withBorder p="md">
          <Text fw={600} fz="lg" mb="md" c={Colors.BluePrimary}>
            Plan Information
          </Text>
          <Table variant="vertical" layout="fixed" withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={200}>Plan ID</Table.Th>
                <Table.Td>
                  <CopyButton value={plan.data.id}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied!" : "Copy to clipboard"}>
                        <Text
                          c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                          className="pointer"
                          onClick={copy}
                        >
                          {plan.data.id}
                        </Text>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Service</Table.Th>
                <Table.Td>
                  <div className="flex-row align-center">
                    <img
                      src={BrandImages.retrieveImage(plan.data.service)}
                      alt=""
                      style={{
                        width: 30,
                        height: 30,
                        objectFit: "contain",
                        marginRight: 10,
                      }}
                    />
                    <Text>{plan.data.service}</Text>
                  </div>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Total Slots</Table.Th>
                <Table.Td>{plan.data.slots}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Used Slots</Table.Th>
                <Table.Td>{plan.data.users.length}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Available Slots</Table.Th>
                <Table.Td>
                  <Text
                    c={
                      hasAvailableSlots()
                        ? Colors.GreenPrimary
                        : Colors.RedPrimary
                    }
                  >
                    {plan.data.slots - plan.data.users.length}
                  </Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Login Email</Table.Th>
                <Table.Td>
                  <CopyButton value={plan.data.login.email}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied!" : "Copy to clipboard"}>
                        <Text
                          c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                          className="pointer"
                          onClick={copy}
                        >
                          {plan.data.login.email}
                        </Text>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Login Password</Table.Th>
                <Table.Td>
                  <CopyButton value={plan.data.login.password}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied!" : "Copy to clipboard"}>
                        <Text
                          c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                          className="pointer"
                          onClick={copy}
                        >
                          {plan.data.login.password}
                        </Text>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Table.Td>
              </Table.Tr>
              {plan.data.service === "iCloud - Apple Music" &&
                plan.data.iCloudStorage && (
                  <Table.Tr>
                    <Table.Th>iCloud Storage</Table.Th>
                    <Table.Td>{plan.data.iCloudStorage}</Table.Td>
                  </Table.Tr>
                )}
              {plan.data.service === "Spotify Premium" &&
                (plan.data as any).address && (
                  <>
                    <Table.Tr>
                      <Table.Th>Address</Table.Th>
                      <Table.Td>{(plan.data as any).address.value}</Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Th>Address Image</Table.Th>
                      <Table.Td>
                        <img
                          src={(plan.data as any).address.image}
                          alt="Address"
                          style={{
                            maxWidth: 200,
                            maxHeight: 150,
                            objectFit: "contain",
                          }}
                        />
                      </Table.Td>
                    </Table.Tr>
                  </>
                )}
            </Table.Thead>
          </Table>
        </Paper>

        <br />

        {/* Users Section */}
        <Paper withBorder p="md">
          <div
            className="flex-row align-center justify-between"
            style={{ marginBottom: 16 }}
          >
            <Text fw={600} fz="lg" c={Colors.BluePrimary}>
              Users in Plan ({plan.data.users.length})
            </Text>
            <Button
              variant="outline"
              onClick={loadPlanUsers}
              loading={isLoadingUsers}
              rightSection={<IconReload size={16} />}
            >
              Refresh Users
            </Button>
          </div>

          {isLoadingUsers ? (
            <div
              className="flex-row align-center justify-center"
              style={{ minHeight: 100 }}
            >
              <MegaLoader size={30} />
            </div>
          ) : planUsers.length > 0 ? (
            <Grid spacing={2} container>
              {planUsers.map((user) => (
                <Grid
                  size={{ xl: 3, lg: 4, md: 6, sm: 6, xs: 12 }}
                  key={user.id}
                >
                  <Card withBorder p="sm">
                    <div className="flex-row align-center justify-between">
                      <div className="flex-row align-center">
                        <IconUser size={20} color={Colors.BluePrimary} />
                        <div style={{ marginLeft: 10 }}>
                          <Text fw={500} fz="sm">
                            {user.name}
                          </Text>
                          <Text c="dimmed" fz="xs">
                            {user.email}
                          </Text>
                        </div>
                      </div>
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => navigate(`/dashboard/user/${user.id}`)}
                        rightSection={<IconEye size={14} />}
                      >
                        View
                      </Button>
                    </div>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <DisplayAlert
              title="No Users"
              children={
                <Text>No users are currently assigned to this plan.</Text>
              }
            />
          )}
        </Paper>
      </div>

      {/* Update Plan Modal */}
      <Modal
        opened={isUpdateModalOpened}
        onClose={closeUpdateModal}
        centered
        size="lg"
        withCloseButton={false}
      >
        <div className="plan-modal flex-col">
          <div className="flex-row align-center width-100 justify-between">
            <div className="flex-row align-center">
              <Text tt="capitalize" c={Colors.BluePrimary} fw={500} fz={17}>
                Update Plan
              </Text>
              <img
                src={BrandImages.retrieveImage(updateForm.values.service)}
                alt=""
                style={{
                  width: 30,
                  height: 30,
                  objectFit: "contain",
                  marginLeft: 10,
                }}
              />
            </div>
            <ActionIcon
              variant="outline"
              color={Colors.BluePrimary}
              onClick={closeUpdateModal}
            >
              <IconX color={Colors.BluePrimary} size={18} />
            </ActionIcon>
          </div>

          <div className="flex-row align-center justify-between width-100">
            <Select
              label="Service"
              {...selectProps}
              {...updateForm.getInputProps("service")}
              w="48%"
              data={ServiceList.map((service) => ({
                value: service,
                label: service,
              }))}
            />
            <NumberInput
              {...updateForm.getInputProps("slots")}
              label="Slots"
              min={1}
              spellCheck={false}
              placeholder="Number of slots"
              w="48%"
            />
          </div>

          <div className="flex-row align-center justify-between width-100">
            <DateInput
              value={dayjs(updateForm.values.subscriptionDate).toDate()}
              onChange={(e) => {
                if (e && e.toString) {
                  updateForm.setFieldValue("subscriptionDate", e?.toString());
                }
              }}
              label="Subscription Date"
              placeholder="Date"
              w="48%"
              spellCheck={false}
            />
            <DateInput
              value={dayjs(updateForm.values.expirationDate).toDate()}
              onChange={(e) => {
                if (e && e.toString) {
                  updateForm.setFieldValue("expirationDate", e?.toString());
                }
              }}
              label="Expiration Date"
              placeholder="Expiration Date"
              w="48%"
              spellCheck={false}
            />
          </div>

          <Divider w="100%" my={20} />

          {updateForm.values.service === "iCloud - Apple Music" && (
            <>
              <Select
                label="iCloud Storage"
                {...selectProps}
                data={iCloudStorageList.map((storage) => ({
                  value: storage,
                  label: storage,
                }))}
                {...updateForm.getInputProps("iCloudStorage")}
                w="100%"
              />
              <Divider w="100%" my={20} />
            </>
          )}

          <TextInput
            {...updateForm.getInputProps("login.email")}
            label="Email"
            placeholder="Enter login email"
            spellCheck={false}
            leftSection={<IconAt size={16} />}
          />

          <div className="flex-row align-end width-100 justify-between">
            <PasswordInput
              {...updateForm.getInputProps("login.password")}
              label="Password"
              w="-webkit-fill-available"
              placeholder="Enter login password"
              spellCheck={false}
              mr={10}
              leftSection={<IconKey size={16} />}
            />
            <Button
              w={180}
              leftSection={<IconRefresh size={18} />}
              color={Colors.BluePrimary}
              onClick={() => {
                updateForm.setFieldValue(
                  "login.password",
                  generateSecurePassword()
                );
              }}
            >
              Generate
            </Button>
          </div>

          {updateForm.values.service === "Spotify Premium" && (
            <>
              <Textarea
                rows={4}
                label="Address"
                spellCheck={false}
                placeholder="Enter house address"
                {...updateForm.getInputProps("address.value")}
                mb={10}
              />
              <Dropzone
                onDrop={(files) => {
                  setIsSpotifyImageUpdated(true);
                  setTempSpotifyImage(files[0]);
                }}
                disabled={isUpdatingPlan}
                maxFiles={1}
                onReject={() => {
                  DefaultErrorNotification("Invalid file or too large!");
                }}
                maxSize={5 * 1024 ** 2}
                accept={IMAGE_MIME_TYPE}
              >
                {tempSpotifyImage || updateForm.values.address.image ? (
                  <div className="temp-image-container flex-row align-center justify-center">
                    <img
                      src={
                        tempSpotifyImage
                          ? URL.createObjectURL(tempSpotifyImage)
                          : updateForm.values.address.image
                      }
                      alt=""
                      style={{
                        maxWidth: 200,
                        maxHeight: 150,
                        objectFit: "contain",
                      }}
                    />
                  </div>
                ) : (
                  <Group
                    justify="center"
                    gap="xl"
                    h={180}
                    style={{ pointerEvents: "none" }}
                  >
                    <div className="flex-col align-center">
                      <Text ta="center" size="lg" inline>
                        Drop Address Image or Click to Upload
                      </Text>
                      <Text ta="center" size="sm" c="dimmed" inline mt={7}>
                        Max file size: 5mb
                      </Text>
                    </div>
                  </Group>
                )}
              </Dropzone>
            </>
          )}

          <div className="flex-row width-100 justify-between align-center">
            <Button
              mt={10}
              w="48%"
              color={Colors.GreenPrimary}
              onClick={handleUpdatePlan}
              loading={isUpdatingPlan}
            >
              Update Plan
            </Button>
            <Button
              mt={10}
              w="48%"
              variant="outline"
              onClick={closeUpdateModal}
              disabled={isUpdatingPlan}
              color={Colors.RedPrimary}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </Container>
  );
};

export default SinglePlan;
